/**
 * Account notices: the at-least-one rule, for the screens.
 *
 * An account is reached about billing (invoices, receipts, failed payments,
 * past-due notices, payment-method reminders) by email, by text message, by
 * a phone call from staff, or by any mix of them, and never by none. The
 * server enforces this for every writer (backend
 * services/communicationPreferences.js, checkAccountNoticeChange); this file
 * is the same rule so a screen can lock the last box and say why BEFORE the
 * person tries.
 *
 * Push notifications are not one of these. They only go one way, so they are
 * a separate on/off.
 */

export type AccountNoticeMethod = "email" | "text" | "call";

export const ACCOUNT_NOTICE_METHODS: readonly AccountNoticeMethod[] = ["email", "text", "call"];

export type AccountNoticeChoice = Record<AccountNoticeMethod, boolean>;

/** Is there an address to reach them on? A method without one is not a method. */
export interface ReachAddresses {
  hasEmail: boolean;
  hasPhone: boolean;
}

export const ACCOUNT_NOTICE_RULE =
  "Every account needs at least one way to be reached about billing: email, text message, or phone call.";

export const LAST_METHOD_NOTE =
  "This is the only way we can reach this account about billing, so it cannot be turned off. Turn on another one first.";

export const NEEDS_PHONE_NOTE = "Needs a phone number on file.";
export const NEEDS_EMAIL_NOTE = "Needs an email address on file.";

const usable = (choice: AccountNoticeChoice, has: ReachAddresses): AccountNoticeChoice => ({
  email: choice.email && has.hasEmail,
  text: choice.text && has.hasPhone,
  call: choice.call && has.hasPhone,
});

const any = (c: AccountNoticeChoice) => c.email || c.text || c.call;

/**
 * May `method` be turned off? No, when it is on and nothing usable would be
 * left without it. (Exactly what the server refuses.)
 */
export function isLastAccountNoticeMethod(
  method: AccountNoticeMethod,
  choice: AccountNoticeChoice,
  has: ReachAddresses = { hasEmail: true, hasPhone: true }
): boolean {
  if (!choice[method]) return false;
  return !any(usable({ ...choice, [method]: false }, has));
}

/** A method that is ticked but has no address to use, so it does not count. */
export function methodLacksAddress(
  method: AccountNoticeMethod,
  has: ReachAddresses = { hasEmail: true, hasPhone: true }
): boolean {
  return method === "email" ? !has.hasEmail : !has.hasPhone;
}

/**
 * The same answer the server would give for a registration: account email
 * can only be off when a text or a phone call was chosen instead.
 */
export function keepAtLeastOne(choice: AccountNoticeChoice): AccountNoticeChoice {
  return any(choice) ? choice : { ...choice, email: true };
}
